/**
 * Shared positional model for movement policies and telemetry.
 *
 * The v0 simulator let the player kite in a straight line forever. That made
 * movement speed change encounter geometry in arbitrary ways instead of buying
 * safer positioning. Here the arena is a real constraint and movement chooses
 * a local step that trades enemy pressure against getting trapped at the edge.
 */

import { BASE_ENEMY_SPEED } from './archetypes.js';
import type { SimState, Vec2 } from './state.js';
import { sweepMovement, zoneDanger } from '../terrain.js';

export const PLAYER_RADIUS_M = 0.4;

/**
 * PROVISIONAL SHAPE CONSTANTS — NOT A RATIFIED BALANCE AUTHORITY.
 *
 * These name the previously-inline literals of the danger/step model so a
 * later calibration has one table to change. They shape only *bot/policy
 * movement preference*; no enemy attack range, speed, or threat value is
 * defined here — those are read from the enemy's own `attackSpec` and
 * `archetype`, which are the authorities.
 */
const POSITIONAL_DANGER_V0 = Object.freeze({
  /** Candidate step length as a fraction of a second of movement. */
  lookaheadS: 0.65,
  /** Evenly spaced candidate directions evaluated per step. */
  directions: 16,
  /** Floor on the closing-distance band over which enemy pressure ramps in. */
  minimumWarningBandM: 1.5,
  /** Warning band as a multiple of the enemy's closing speed. */
  warningBandSecondsOfClosing: 1.5,
  /** Fraction of the arena radius at which edge danger starts accruing. */
  edgeStartFractionOfArena: 0.72,
  /** Floor on the edge band width, so a tiny arena still has a gradient. */
  minimumEdgeBandM: 0.5,
  /** Danger contributed at the very edge, in the same HP/s-like units. */
  edgeDangerAtWall: 18,
});


export function magnitude(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

export function clampToArena(pos: Vec2, arenaRadiusM: number, marginM = PLAYER_RADIUS_M): Vec2 {
  const allowed = Math.max(0, arenaRadiusM - marginM);
  const r = magnitude(pos);
  if (r <= allowed || r < 1e-9) return pos;
  const scale = allowed / r;
  return { x: pos.x * scale, y: pos.y * scale };
}

/**
 * Instantaneous positional danger in HP/s-like units.
 *
 * Enemy pressure rises as an enemy approaches its attack range. The edge term
 * represents lost escape routes: hugging the wall can be locally safe for one
 * tick, but is strategically dangerous because the next retreat is blocked.
 */
export function positionalDanger(state: Pick<SimState, 'enemies' | 'arenaRadiusM' | 'terrain'>, pos: Vec2): number {
  let danger = 0;
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const d = Math.hypot(enemy.pos.x - pos.x, enemy.pos.y - pos.y);
    // The enemy's own resolved attack range is the single authority here —
    // never a second archetype-id-keyed table, which silently ignored any
    // per-spawn `attack.rangeM` override.
    const attackRange = enemy.attackSpec.rangeM;
    const closingSpeed = BASE_ENEMY_SPEED * enemy.archetype.moveMult;
    const warningBand = Math.max(
      POSITIONAL_DANGER_V0.minimumWarningBandM,
      closingSpeed * POSITIONAL_DANGER_V0.warningBandSecondsOfClosing,
    );
    const exposure = d <= attackRange ? 1 : Math.max(0, 1 - (d - attackRange) / warningBand);
    danger += enemy.archetype.threatPerS * exposure * exposure;
  }

  const radius = magnitude(pos);
  const edgeStart = state.arenaRadiusM * POSITIONAL_DANGER_V0.edgeStartFractionOfArena;
  const edgeWidth = Math.max(POSITIONAL_DANGER_V0.minimumEdgeBandM, state.arenaRadiusM - PLAYER_RADIUS_M - edgeStart);
  const edge = Math.max(0, (radius - edgeStart) / edgeWidth);
  danger += POSITIONAL_DANGER_V0.edgeDangerAtWall * edge * edge;
  danger += zoneDanger(state.terrain, pos);
  return danger;
}

/** Pick the safest local direction. Returns null when holding is already best. */
export function safestStep(state: SimState): Vec2 | null {
  const from = state.player.pos;
  const stride = state.player.baseSpeed * POSITIONAL_DANGER_V0.lookaheadS;
  let bestDanger = positionalDanger(state, from);
  let best: Vec2 | null = null;

  for (let i = 0; i < POSITIONAL_DANGER_V0.directions; i++) {
    const angle = (i * 2 * Math.PI) / POSITIONAL_DANGER_V0.directions;
    const direction = { x: Math.cos(angle), y: Math.sin(angle) };
    const candidate = sweepMovement(
      state.terrain,
      from,
      { x: direction.x * stride, y: direction.y * stride },
      PLAYER_RADIUS_M,
    ).position;
    const score = positionalDanger(state, candidate);
    if (score < bestDanger - 1e-6) {
      bestDanger = score;
      best = direction;
    }
  }
  return best;
}
